import type { Map as MapLibreMap } from "maplibre-gl";
import type { CameraPadding } from "../domain/camera-plan";

const MARKER_CLEARANCE_PIXELS = 24;
const MAX_CORRECTION_PASSES = 4;
const CORRECTION_EPSILON_PIXELS = 0.5;

/** Dimensions and edge clearance for one rendered map frame. */
export interface MapFrame {
  readonly height: number;
  readonly padding: CameraPadding;
  readonly width: number;
}

/** Minimal MapLibre surface required to correct a subject's screen position. */
type FramingMap = Pick<MapLibreMap, "getCenter" | "jumpTo" | "project" | "unproject">;

/** A point measured from the top-left corner of the map viewport. */
export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Keeps the rider clear of map edges and navigation controls.
 *
 * @param width - Current map container width in CSS pixels.
 * @param height - Current map container height in CSS pixels.
 * @returns Responsive padding for the unobstructed map area.
 */
export function routePadding(width: number, height: number, layout: "preview" | "video" = "video"): CameraPadding {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new RangeError("Map dimensions must be finite and positive.");
  }
  // Ride information and statistics occupy separate rows outside both map views.
  const padding = { bottom: 32, left: 32, right: layout === "preview" ? 48 : 32, top: 32 };
  const horizontalScale = Math.min(1, Math.max(0, width - 96) / (padding.left + padding.right));
  const verticalScale = Math.min(1, Math.max(0, height - 96) / (padding.top + padding.bottom));
  return {
    bottom: padding.bottom * verticalScale, top: padding.top * verticalScale,
    left: padding.left * horizontalScale, right: padding.right * horizontalScale,
  };
}

/**
 * Keeps one geographic subject inside the map's usable screen rectangle.
 *
 * MapLibre performs the first placement with pitch-aware padding. Camera
 * treatments may deliberately look ahead or laterally, so this bounded
 * correction uses MapLibre's own project/unproject transform as a final guard.
 *
 * @param map - Ready MapLibre map after its camera pose has been applied.
 * @param coordinate - Subject longitude and latitude.
 * @param frame - Current map dimensions and overlay padding.
 */
export function keepCoordinateInSafeArea(
  map: FramingMap,
  coordinate: [longitude: number, latitude: number],
  frame: MapFrame,
): void {
  for (let pass = 0; pass < MAX_CORRECTION_PASSES; pass += 1) {
    const subject = map.project(coordinate);
    const target = clampScreenPoint(subject, frame);
    const deltaX = subject.x - target.x;
    const deltaY = subject.y - target.y;
    if (
      Math.abs(deltaX) <= CORRECTION_EPSILON_PIXELS
      && Math.abs(deltaY) <= CORRECTION_EPSILON_PIXELS
    ) {
      return;
    }

    // Recenter through MapLibre's perspective transform. Repeating the
    // correction accommodates the non-linear ground projection at high pitch.
    const center = map.project(map.getCenter());
    const correctedCenter = map.unproject([center.x + deltaX, center.y + deltaY]);
    map.jumpTo({ center: [correctedCenter.lng, correctedCenter.lat] });
  }
}

/**
 * Clamps a screen point to the visible map area with marker-halo clearance.
 *
 * @param point - Projected screen position.
 * @param frame - Current map dimensions and overlay padding.
 * @returns The nearest position inside the safe rectangle.
 * @throws RangeError when the frame leaves no usable map area.
 */
export function clampScreenPoint(point: ScreenPoint, frame: MapFrame): ScreenPoint {
  const left = frame.padding.left + MARKER_CLEARANCE_PIXELS;
  const right = frame.width - frame.padding.right - MARKER_CLEARANCE_PIXELS;
  const top = frame.padding.top + MARKER_CLEARANCE_PIXELS;
  const bottom = frame.height - frame.padding.bottom - MARKER_CLEARANCE_PIXELS;
  if (
    ![point.x, point.y, left, right, top, bottom].every(Number.isFinite)
    || left > right
    || top > bottom
  ) {
    throw new RangeError("Map frame must contain a finite marker-safe area.");
  }
  return {
    x: clamp(point.x, left, right),
    y: clamp(point.y, top, bottom),
  };
}

/** Numeric bounds helper. */
function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
